import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Clock, Sun, Umbrella, AlertCircle, Wallet, ChevronRight, X, Gift, CheckCircle2 } from 'lucide-react';
import type { PayrollMonthlyRecord, EmployeePaidHours, EmployeeRateHistory } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { paidHoursService } from '@/services/paid-hours.service';
import { StatTile } from '@/components/ui/stat-tile';
import { AccountSalaryGate } from '@/components/payroll/AccountSalaryGate';

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-08-01` → `Aug 2026`. */
function formatRateMonth(date: string): string {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return `${MONTH_NAMES[month - 1] ?? month} ${year}`;
}

/**
 * A period starting in 1900 is the baseline a first-ever rate was back-dated to — it
 * means "from the beginning", not January 1900. Same rule the admin rate list applies.
 */
function formatRateRange(period: EmployeeRateHistory): string {
  const isBaseline = period.effective_from.startsWith('1900');
  const from = isBaseline ? 'Initial' : formatRateMonth(period.effective_from);

  if (!period.effective_to) return isBaseline ? 'Initial rate' : `${from} onwards`;
  if (period.effective_to === period.effective_from) return from;
  return `${from} – ${formatRateMonth(period.effective_to)}`;
}

function currentMonthLabel(): string {
  const now = new Date();
  return `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
}

/**
 * The gate wraps the content rather than sitting inside it, so none of the queries
 * below even fire while the page is locked — pay figures are not fetched until the
 * employee has unlocked with their own key.
 */
export function AccountSalaryPage() {
  return (
    <AccountSalaryGate>
      <AccountSalaryContent />
    </AccountSalaryGate>
  );
}

function AccountSalaryContent() {
  const [detailRecord, setDetailRecord] = useState<PayrollMonthlyRecord | null>(null);

  const { data: history, isLoading } = useQuery({
    queryKey: ['payroll', 'salary', 'me'],
    queryFn: () => payrollService.getMySalaryHistory(),
  });

  const now = new Date();
  const isThisMonth = (r: PayrollMonthlyRecord) =>
    r.year === now.getFullYear() && r.month === now.getMonth() + 1;

  // Matched by month, not taken as the first row: the hero is labelled with the
  // current month, so it must show that month's figures and nothing else.
  const latest = history?.find(isThisMonth) ?? null;

  const { data: myPaidHours } = useQuery({
    queryKey: ['paid-hours', 'me', now.getFullYear(), now.getMonth() + 1],
    queryFn: () => paidHoursService.listMy({ year: now.getFullYear(), month: now.getMonth() + 1 }),
  });
  const paidHoursList = myPaidHours ?? [];

  // Read through our own backend, not the Rate API. An employee's key is `scope: own`,
  // which the live API confirms can only read rates that key itself created — so it
  // returns nothing for rates an admin set. This endpoint is permission-gated on
  // SALARY_VIEW_OWN and already returns exactly this employee's periods.
  const { data: rateHistory, isLoading: ratesLoading } = useQuery({
    queryKey: ['payroll', 'rate-history', 'me'],
    queryFn: () => payrollService.getMyRateHistory(),
  });

  return (
    <div className="space-y-4">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 px-5 py-4 text-white shadow-md">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10 blur-2xl" />

        <div className="relative flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-200">
              {currentMonthLabel()} Net Salary
            </p>
            {isLoading ? (
              <p className="mt-0.5 text-2xl font-bold">Loading…</p>
            ) : latest ? (
              <>
                <p className="mt-0.5 text-3xl font-bold">{formatCurrency(latest.net_amount)}</p>
                <p className="mt-1 text-xs text-teal-100">
                  Gross {formatCurrency(latest.gross_amount)}
                  <span className="mx-1.5 opacity-50">·</span>
                  ₹{parseFloat(latest.per_hour_rate_snapshot).toFixed(0)}/hr
                </p>
              </>
            ) : (
              <>
                <p className="mt-0.5 text-2xl font-bold text-teal-200">—</p>
                <p className="mt-0.5 text-xs text-teal-200">Salary not computed yet</p>
              </>
            )}
          </div>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/20">
            <Wallet className="h-5 w-5" />
          </div>
        </div>
      </div>

      {/* This month stat cards */}
      {latest && (() => {
        const latestPaidMins = (latest.paid_hours_minutes as number | undefined) ?? 0;
        const latestPaidAmt  = parseFloat((latest.paid_hours_amount as string | undefined) ?? '0');
        // Read, not re-added: total_payable_minutes is the server's own figure and is
        // already net of the penalty hours. Summing the tiles here again is how this
        // page and the payroll screens used to end up one penalty apart.
        const totalMinutes = latest.total_payable_minutes;
        // Sunday pay: each Sunday's first 9h capped at the configured rate, that day's excess at 1x
        // — computed server-side per individual Sunday (payroll.service.ts), not re-derived here.
        const sundayCapped = latest.sunday_capped_minutes;
        const sundayExcess = latest.sunday_excess_minutes;
        const sundayRate = sundayCapped > 0
          ? (latest.sunday_effective_minutes - sundayExcess) / sundayCapped
          : 2;
        return (
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              This Month — Hours
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              <StatTile label="Working Hours" value={formatHours(latest.working_minutes)} sub="Mon–Sat" icon={Clock} tone="sky" />
              <StatTile label="Grace Hours" value={formatHours(latest.grace_minutes)} sub="Bonus" icon={Clock} tone="emerald" />
              <StatTile
                label="Sunday Hours"
                value={formatHours(latest.sunday_effective_minutes)}
                sub={sundayExcess > 0
                  ? `${formatHours(sundayCapped)} ×${sundayRate.toFixed(0)}x + ${formatHours(sundayExcess)} ×1x`
                  : `${formatHours(sundayCapped)} ×${sundayRate.toFixed(0)}x`}
                icon={Sun}
                tone="amber"
              />
              <StatTile label="Paid Leave" value={formatHours(latest.paid_leave_minutes)} sub="Approved" icon={Umbrella} tone="violet" />
              <StatTile label="Penalty" value={formatHours(latest.penalty_minutes)} sub={latest.penalty_minutes > 0 ? 'Deducted' : '—'} icon={AlertCircle} tone="rose" />
              <StatTile label="Company Paid" value={formatHours(latestPaidMins)} sub={latestPaidMins > 0 ? (latestPaidAmt > 0 ? `+${formatCurrency(latestPaidAmt)}` : 'Approved') : '—'} icon={Gift} tone="teal" />
              <StatTile label="Total Hours" value={formatHours(Math.max(0, totalMinutes))} sub="Net of penalty" icon={CheckCircle2} tone="blue" />
            </div>
          </div>
        );
      })()}

      {/* Company Paid Hours section */}
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          This Month — Company Paid
        </p>
        {paidHoursList.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 bg-card px-4 py-4 text-center text-sm text-muted-foreground">
            No company paid hours for this month
          </div>
        ) : (
          <div className="space-y-2">
            {paidHoursList.map((r) => (
              <PaidHoursRow key={r.id} record={r} />
            ))}
          </div>
        )}
      </div>

      {/* Monthly history */}
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Monthly History
        </p>

        {isLoading ? (
          <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : !history || history.length === 0 ? (
          <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            No salary records yet
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((record) => (
              <HistoryRow
                key={record.id}
                record={record}
                isCurrent={isThisMonth(record)}
                onClick={() => setDetailRecord(record)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rate history */}
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          My Hourly Rate
        </p>

        {ratesLoading ? (
          <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : !rateHistory || rateHistory.length === 0 ? (
          <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            No rate set yet
          </div>
        ) : (
          <div className="space-y-2">
            {rateHistory.map((period, idx) => (
              <div
                key={period.id}
                className="flex items-center justify-between rounded-lg border border-border/60 bg-card px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    ₹{parseFloat(period.per_hour_rate).toFixed(2)}/hr
                  </span>
                  {idx === 0 && (
                    <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">
                      Current
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{formatRateRange(period)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <SalaryDrawer
        record={detailRecord}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
      />
    </div>
  );
}

function PaidHoursRow({ record }: { record: EmployeePaidHours }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 border border-teal-100 dark:bg-teal-900/30 dark:border-teal-700/40">
        <Gift className="h-4 w-4 text-teal-600 dark:text-teal-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{record.reason}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{formatDate(record.attendance_date)}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-teal-700 dark:text-teal-400">
          +{parseFloat(record.hours).toFixed(1)}h
        </p>
        <span className="inline-flex rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
          Approved
        </span>
      </div>
    </div>
  );
}


function HistoryRow({
  record, isCurrent, onClick,
}: {
  record: PayrollMonthlyRecord; isCurrent: boolean; onClick: () => void;
}) {
  const monthName = MONTH_NAMES[(record.month - 1)] ?? '';
  const workHours  = formatHours(record.working_minutes + record.grace_minutes + record.sunday_effective_minutes);
  const leaveHours = formatHours(record.paid_leave_minutes);
  const rowPaidMins = (record.paid_hours_minutes as number | undefined) ?? 0;

  return (
    <button
      onClick={onClick}
      className="w-full rounded-lg border border-border/60 bg-card px-3 py-2.5 text-left transition hover:shadow-sm hover:bg-muted/30"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40">
          <CalendarDays className="h-4 w-4 text-teal-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{monthName} {record.year}</span>
            {isCurrent && (
              <span className="inline-flex rounded-full bg-teal-100 dark:bg-teal-900/30 px-2 py-0.5 text-[10px] font-medium text-teal-700 dark:text-teal-400">
                Current
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Work {workHours} · Leave {leaveHours}{rowPaidMins > 0 ? ` · Paid +${formatHours(rowPaidMins)}` : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-teal-700 dark:text-teal-400">{formatCurrency(record.net_amount)}</p>
          <p className="text-[11px] text-muted-foreground">Gross {formatCurrency(record.gross_amount)}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </button>
  );
}

function SalaryDrawer({ record, open, onClose }: {
  record: PayrollMonthlyRecord | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      {/* Drawer panel */}
      <div
        className={`fixed inset-x-0 bottom-0 z-modal max-h-[88vh] overflow-y-auto rounded-t-2xl bg-card shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {record && <DrawerContent record={record} onClose={onClose} />}
      </div>
    </>
  );
}

function DrawerContent({ record, onClose }: { record: PayrollMonthlyRecord; onClose: () => void }) {
  const monthName = MONTH_NAMES[(record.month - 1)] ?? '';
  const gross   = parseFloat(record.gross_amount);
  const net     = parseFloat(record.net_amount);
  const loanDed      = parseFloat(record.loan_deduction);
  const advDed       = parseFloat(record.advance_deduction);
  const penaltyDed   = parseFloat(record.penalty_deduction ?? '0');
  const rate         = parseFloat(record.per_hour_rate_snapshot);
  const drawerPaidMins = (record.paid_hours_minutes as number | undefined) ?? 0;
  const drawerPaidAmt  = parseFloat((record.paid_hours_amount as string | undefined) ?? '0');

  return (
    <div>
      {/* Handle bar integrated into header — no white gap above teal */}
      <div className="bg-teal-800 px-5 pt-3 pb-3 text-white">
        <div className="mx-auto mb-2.5 h-1 w-10 rounded-full bg-white/20" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <span className="font-semibold">{monthName} {record.year}</span>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3">
        {/* Gross + Net — compact inline row */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border bg-card px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Gross</p>
            <p className="mt-0.5 text-xl font-bold">{formatCurrency(gross)}</p>
          </div>
          <div className="rounded-lg border bg-teal-50 dark:bg-teal-900/20 px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-teal-600 dark:text-teal-400">Net (take-home)</p>
            <p className="mt-0.5 text-xl font-bold text-teal-800 dark:text-teal-200">{formatCurrency(net)}</p>
          </div>
        </div>

        {/* Company Paid Hours credit */}
        {drawerPaidAmt > 0 && (
          <div className="rounded-lg border border-teal-100 bg-teal-50 dark:border-teal-700/40 dark:bg-teal-900/20 px-3 py-2 text-sm">
            <p className="font-medium text-teal-700 dark:text-teal-300 mb-0.5 flex items-center gap-1.5">
              <Gift className="h-3.5 w-3.5" /> Company Paid Hours
            </p>
            <p className="text-xs text-teal-600 dark:text-teal-400">
              +{formatCurrency(drawerPaidAmt)} ({formatHours(drawerPaidMins)})
            </p>
          </div>
        )}

        {/* Deductions */}
        {(loanDed > 0 || advDed > 0 || penaltyDed > 0) && (
          <div className="rounded-lg border border-red-100 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm">
            <p className="font-medium text-red-700 dark:text-red-400 mb-0.5">Deductions</p>
            {loanDed > 0    && <p className="text-xs text-red-600 dark:text-red-400">Loan EMI: {formatCurrency(loanDed)}</p>}
            {advDed  > 0    && <p className="text-xs text-red-600 dark:text-red-400">Advance: {formatCurrency(advDed)}</p>}
            {penaltyDed > 0 && <p className="text-xs text-red-600 dark:text-red-400">Penalty: {formatCurrency(penaltyDed)}</p>}
          </div>
        )}

        {/* Hours breakdown */}
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Hours Breakdown
          </p>
          <div className={`grid gap-2 ${drawerPaidMins > 0 ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-3 sm:grid-cols-5'}`}>
            <StatTile label="Working" value={formatHours(record.working_minutes)} sub="Mon–Sat" icon={Clock} tone="sky" />
            <StatTile label="Grace"   value={formatHours(record.grace_minutes)}   sub="Bonus" icon={Clock} tone="emerald" />
            <StatTile label="Sunday"  value={formatHours(record.sunday_effective_minutes)} sub={`${formatHours(record.sunday_raw_minutes)} ×2`} icon={Sun} tone="amber" />
            <StatTile label="Leave"   value={formatHours(record.paid_leave_minutes)} sub="Approved" icon={Umbrella} tone="violet" />
            <StatTile label="Penalty" value={formatHours(record.penalty_minutes)}  sub={record.penalty_minutes > 0 ? 'Deducted' : '—'} icon={AlertCircle} tone="rose" />
            {drawerPaidMins > 0 && (
              <StatTile label="Paid" value={formatHours(drawerPaidMins)} sub={drawerPaidAmt > 0 ? `+${formatCurrency(drawerPaidAmt)}` : 'Approved'} icon={Gift} tone="teal" />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1.5 border-t border-border/40 pt-2 pb-1 text-xs text-muted-foreground">
          <Wallet className="h-3.5 w-3.5" />
          Per Hour Rate: ₹{rate.toFixed(2)}/hr
        </div>
      </div>
    </div>
  );
}
